import { H2, InputText } from "@sellar-io/components";
import { useShallow } from "zustand/react/shallow";
import { InputLabel, InputContainer } from "@sellar-io/components";

import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  BuyerCreationFlowStep,
  useBuyerCreationFlowStore,
} from "./BuyerCreationFlowStore";

export const BuyerSignupGooglePlacesSearchStep = () => {
  const { setStep, setSearchGooglePlacesResult } = useBuyerCreationFlowStore(
    useShallow((state) => ({
      setStep: state.setStep,
      setSearchGooglePlacesResult: state.setSearchGooglePlacesResult,
    })),
  );

  const selectPlace = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      if (place?.address_components) {
        setSearchGooglePlacesResult(place);
        setStep(BuyerCreationFlowStep.BuyerBusinessFormStep);
      }
    },
    [setSearchGooglePlacesResult, setStep],
  );

  return (
    <div className={"flex h-full flex-col justify-center"}>
      <div className="flex flex-col gap-4">
        <div>
          <H2 className={"text-center"}>Your delivery address</H2>
          <p className={"text-center"}>
            If you have multiple sites, you can add them later
          </p>
        </div>

        <APIProvider
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          libraries={["places"]}
        >
          <div className="autocomplete-control">
            <PlaceAutocomplete onPlaceSelect={selectPlace} />
          </div>
        </APIProvider>

        <p className="text-zinc-500">
          Or you can{" "}
          <span
            className="cursor-pointer underline"
            onClick={() => setStep(BuyerCreationFlowStep.BuyerBusinessFormStep)}
          >
            enter your address manually
          </span>
        </p>
      </div>
    </div>
  );
};

interface PlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
}

const PlaceAutocomplete = ({
  onPlaceSelect,
  setSearchLength,
}: PlaceAutocompleteProps & { setSearchLength?: (length: number) => void }) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ["geometry", "name", "formatted_address", "address_components"],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener("place_changed", () => {
      onPlaceSelect(placeAutocomplete.getPlace());
    });
  }, [onPlaceSelect, placeAutocomplete]);

  return (
    <div className="autocomplete-container">
      <>
        <InputLabel>Enter search term</InputLabel>
        <InputContainer>
          <InputText
            ref={inputRef}
            onChange={(e) => setSearchLength?.(e.target.value.length)}
          />
        </InputContainer>
      </>
    </div>
  );
};
